import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useEffect } from 'react';
import { noop } from 'lodash';
import { useForm } from '../../../components/Form/hooks';
import {
  OperacaoForm,
  defaultValues,
  tiposFilter,
  validationSchema,
} from './form-controller';
import Agro1Loader from '../../../components/Agro1Loader';
import { OperacoesService } from '../../../services';
import GrupoInsumoService from '../../../services/grupos-insumo';
import GrupoMaquinaService from '../../../services/grupos-maquina';
import { OperacaoToSave } from '../../../services/operacoes';
import Agro1Toast from '../../../components/Agro1Toast';
import useAsyncFetch from '../../../hooks/use-async';
import { beforeExit } from '../../utils';
import { OperacaoFormValues } from '../types';
import { getMessagesOperacoes } from '../utils';

const OperacaoCreateEditPage = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formObject = useForm<OperacaoFormValues>({
    validationSchema,
    defaultValues,
  });
  const { reset, handleSubmit, getValues } = formObject.form;

  const exit = () => {
    navigate('/gerenciador-dados/operacoes');
  };

  const { data: gruposMaquina } = useAsyncFetch(
    {
      withLoader: true,
      callback: () => GrupoMaquinaService.list(),
      errorMessage: 'Falha ao carregar grupos de máquinas.',
      onError: exit,
    },
    [],
  );

  const { data: gruposInsumos } = useAsyncFetch(
    {
      withLoader: true,
      callback: () => GrupoInsumoService.list(),
      errorMessage: 'Falha ao carregar grupos de insumos.',
      onError: exit,
    },
    [],
  );

  const { data: operacao } = useAsyncFetch(
    {
      withLoader: true,
      callback: async () => {
        if (params.id) return OperacoesService.find(params.id);
      },
      errorMessage: 'Falha ao carregar o registro para edição.',
      onError: exit,
    },
    [params.id],
  );

  useEffect(() => {
    if (!operacao) return;

    reset({
      id: operacao.id,
      descricao: operacao.descricao,
      statusOperacao: operacao.statusOperacao,
      grupoMaquinas: operacao.grupoMaquinas,
      grupoInsumos: operacao.grupoInsumos,
    });
  }, [operacao, reset, getValues]);

  const onSubmit = async () => {
    const formValue = getValues();
    const loaderId = Agro1Loader.show();

    try {
      const record: OperacaoToSave = {
        descricao: formValue.descricao!.toUpperCase(),
        statusOperacao: formValue.statusOperacao,
        grupoMaquinas: formValue.grupoMaquinas.map(({ id }) => id),
        grupoInsumos: formValue.grupoInsumos.map(({ id }) => ({
          tipo: id[0],
          codigo: id[1],
        })),
        ordemAplicacao: 1,
      };

      if (params.id) {
        await OperacoesService.update({ ...record, id: params.id });
      } else {
        await OperacoesService.create(record);
      }

      exit();

      Agro1Toast.show({
        detail: 'Registro salvo com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      Agro1Toast.show({
        detail: getMessagesOperacoes(error, params.id ? 'edit' : 'create'),
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  };

  return (
    <div className="container w-full gerenciador-dados-create-edit-container">
      <div className="flex flex-wrap mt-6 mx-3">
        <div className="text-3xl font-normal">
          {operacao ? 'Editar' : 'Nova'} Operação de Campo
        </div>
      </div>
      <ConfirmDialog />
      <div>
        <Panel className="mt-4 mx-3">
          <div className="col-12 m-auto">
            <OperacaoForm
              formObject={formObject}
              submitByEnter={false}
              onSubmit={noop}
            >
              <div className="col-12 flex align-items-center pb-0">
                <OperacaoForm.InputTextarea
                  name="descricao"
                  label="Descrição:"
                  containerClassName="col-6"
                  className="w-full"
                  rows={1}
                />
                <OperacaoForm.Dropdown
                  name="statusOperacao"
                  label="Tipo Operação:"
                  options={tiposFilter}
                  dataKey="id"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Selecione"
                  filter={false}
                  containerClassName="col-6"
                />
              </div>
              <div className="col-12 flex">
                <OperacaoForm.MultiSelect
                  name="grupoMaquinas"
                  label="Grupo de Máquinas:"
                  options={gruposMaquina}
                  dataKey="id"
                  optionLabel="descricao"
                  placeholder="Selecione"
                  containerClassName="col-6 w-6"
                />
                <OperacaoForm.MultiSelect
                  name="grupoInsumos"
                  label="Grupo de Insumos:"
                  options={gruposInsumos}
                  dataKey="itemId"
                  optionLabel="descricao"
                  placeholder="Selecione"
                  containerClassName="col-6 w-6"
                />
              </div>
            </OperacaoForm>
          </div>
        </Panel>
        <div className="flex justify-content-end mt-2 mx-3">
          <Button
            type="button"
            className="button-danger mr-1"
            label="Cancelar"
            onClick={() => beforeExit({ onConfirm: exit })}
          />
          <Button
            type="submit"
            className="button-secondary"
            label="Salvar"
            onClick={() => handleSubmit(onSubmit)()}
          />
        </div>
      </div>
    </div>
  );
};

export default OperacaoCreateEditPage;
